import { EventEditModal } from "@/components/event/EventEditModal.tsx";
import { linkifyText } from "@/helpers/utils.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeeMe, useEmployeesByIds } from "@/queries/employees/use-employees.query.ts";
import { useEventsDelete, useEventsResponse } from "@/queries/use-events.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCalendarProviders } from "@/types/calendar.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiEvent,
  ApiEventAttendee,
  ApiEventAttendeeResponseStatuses,
  ApiEventEdit,
  ApiEventVisibility,
  getDescriptionForAttendee,
} from "@/types/event.types.ts";
import { JobStatusItems } from "@/types/recruit/job.types.ts";
import {
  Button,
  ButtonLink,
  ConfirmationModal,
  DotSeparator,
  FontAwesomeIcon,
  HtmlViewer,
  ListItem,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { toZonedTime } from "date-fns-tz";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export interface EventPreviewModalProps extends ModalProps {
  event: ApiEvent;
}

export function EventPreviewModal({ event, ...props }: EventPreviewModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { renderDate, renderTime, renderEmployeeName } = useFormatter();
  const zonedStart = toZonedTime(event.datetime_start, event.timezone);
  const zonedEnd = toZonedTime(event.datetime_end, event.timezone);
  const modalsService = useModals();
  const { data: employee } = useEmployeeMe();
  const useEventDelete = useEventsDelete();
  const eventResponse = useEventsResponse();

  const [selfResponseStatus, setSelfResponseStatus] = useState<ApiEventAttendeeResponseStatuses>();
  const { data: usersLoaded } = useEmployeesByIds(
    event.attendees.filter((x) => x.employee).map((x) => x.employee?.id || 0)
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.events.viewed, {
      [analyticProperties.id]: event.id,
    });
  });

  const attendees = useMemo(() => {
    const selfIndex = event.attendees.findIndex((x) => x.employee?.id == employee?.id);
    return event.attendees.map((attendee, index) => {
      const user = attendee.employee && usersLoaded?.find((x) => x.id == attendee.employee?.id);
      const withEmployee = user ? { ...attendee, runtime_only_employee: user } : attendee;
      return index === selfIndex && selfResponseStatus
        ? { ...withEmployee, response_status: selfResponseStatus }
        : withEmployee;
    });
  }, [event.attendees, usersLoaded, employee?.id, selfResponseStatus]);

  const handleEdit = (event: ApiEvent) => {
    if (event.external_event_link) {
      window.open(event.external_event_link, "_blank");
      return;
    }

    const eventToEdit: ApiEventEdit = {
      ...event,
    };

    modalsService.openModal(EventEditModal, {
      event: eventToEdit,
      eventId: event?.id,
    });
  };

  const handleDelete = useCallback(() => {
    modalsService.openModal(ConfirmationModal, {
      question: t("calendar:event_delete"),
      text: t("calendar:event_delete_description"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await useEventDelete.mutateAsync(event.id);

          analyticsService.trackEvent(analyticEvents.events.deleted, {
            [analyticProperties.id]: event.id,
            [analyticProperties.actionType]: "Calendar",
          });

          close(id);
        } catch {
          return;
        }
      },
    });
  }, [event]);

  const attendeeResponseStatus = (attendee: ApiEventAttendee) => {
    if (attendee.response_status == ApiEventAttendeeResponseStatuses.accepted) {
      return <Text className={"text-positive"}>{t("calendar:response_status_accepted")}</Text>;
    } else if (attendee.response_status == ApiEventAttendeeResponseStatuses.needs_action) {
      return <Text className={"text-warning"}>{t("calendar:response_status_needs_action")}</Text>;
    } else if (attendee.response_status == ApiEventAttendeeResponseStatuses.tentative) {
      return (
        <Text className={"text-warning"}>{t("calendar:response_status_needs_tentative")}</Text>
      );
    } else if (attendee.response_status == ApiEventAttendeeResponseStatuses.declined) {
      return <Text className={"text-danger"}>{t("calendar:response_status_needs_declined")}</Text>;
    }
    return null;
  };

  const selfAttendee = useMemo(
    () => attendees.find((x) => x.employee?.id == employee?.id),
    [attendees, employee]
  );

  const canResponse = useMemo(() => {
    return selfAttendee && event.calendar.provider == ApiCalendarProviders.system;
  }, [selfAttendee, event]);

  const handleResponse = useCallback(
    async (response: ApiEventAttendeeResponseStatuses) => {
      if (!canResponse || !selfAttendee) {
        return;
      }

      try {
        await eventResponse.mutateAsync({
          id: event.id,
          response: response,
        });
        analyticsService.trackEvent(analyticEvents.events.responded, {
          [analyticProperties.id]: event.id,
          [analyticProperties.actionType]: response,
        });
        if (selfAttendee) setSelfResponseStatus(response);
      } catch {
        return;
      }
    },
    [selfAttendee]
  );

  return (
    <Modal
      {...props}
      layout="base"
      size={"md"}
      title={event.title}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          {canResponse && selfAttendee && (
            <Stack gap={"md"} justify={"between"} direction={"horizontal"} className={"mb-sm"}>
              <Title header={t("calendar:going_question")} size={"xs"} />
              <Stack direction={"horizontal"} gap={"sm"}>
                <Button
                  size={"sm"}
                  onClick={() => {
                    handleResponse(ApiEventAttendeeResponseStatuses.accepted);
                  }}
                  variant={
                    selfAttendee.response_status == ApiEventAttendeeResponseStatuses.accepted
                      ? "primary"
                      : "secondary"
                  }
                >
                  {t("common:yes")}
                </Button>
                <Button
                  size={"sm"}
                  onClick={() => {
                    handleResponse(ApiEventAttendeeResponseStatuses.declined);
                  }}
                  variant={
                    selfAttendee.response_status == ApiEventAttendeeResponseStatuses.declined
                      ? "primary"
                      : "secondary"
                  }
                >
                  {t("common:no")}
                </Button>
                <Button
                  size={"sm"}
                  onClick={() => {
                    handleResponse(ApiEventAttendeeResponseStatuses.tentative);
                  }}
                  variant={
                    selfAttendee.response_status == ApiEventAttendeeResponseStatuses.tentative
                      ? "primary"
                      : "secondary"
                  }
                >
                  {t("common:maybe")}
                </Button>
              </Stack>
            </Stack>
          )}
          <Button onClick={() => close(id)} size={"lg"} className={"w-full"}>
            {t("common:close")}
          </Button>
          {employee?.id == event.organizer.employee?.id && event.recurrence.length == 0 && (
            <Button
              onClick={() => {
                close(id);
                handleEdit(event);
              }}
              leftIcon={
                event.external_event_link ? (
                  <FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />
                ) : undefined
              }
              size={"lg"}
              variant={"secondary"}
              className={"w-full"}
            >
              {t("common:edit")}
            </Button>
          )}
          {employee?.id == event.organizer.employee?.id && event.recurrence.length == 0 && (
            <Button onClick={handleDelete} size={"lg"} variant={"danger"} className={"w-full"}>
              {t("common:delete")}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack>
        <ListItem
          preTitle={t("common:date")}
          title={
            event.is_all_day
              ? `${renderDate(event.datetime_start)}— ${renderDate(event.datetime_end)}`
              : `${renderDate(zonedStart)} · ${renderTime(zonedStart)} — ${renderTime(zonedEnd)}`
          }
          caption={event.timezone}
        />
        {event.conference_data && (
          <ListItem
            preTitle={event.conference_data.title}
            title={event.conference_data.label}
            valueSlot={
              <ButtonLink
                onClick={() => {
                  analyticsService.trackEvent(analyticEvents.events.joinedConference, {
                    [analyticProperties.id]: event.id,
                    [analyticProperties.source]: event.conference_data?.label,
                  });
                }}
                blank
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-video"} />}
                href={event.conference_data.uri}
              >
                {t("calendar:join")}
              </ButtonLink>
            }
          />
        )}
        {event.description && (
          <ListItem
            preTitle={t("common:description")}
            titleSlot={<HtmlViewer content={linkifyText(event.description)} />}
          />
        )}
        {event.interview && (
          <>
            <Title header={t("ats:job")} size={"xs"} paddingBottom paddingTop />
            <ListItem
              to={`/recruitment/candidates/${event.interview.applicant_id}?job_id=${event.interview.job.id}`}
              blank={true}
              title={event.interview.job.title}
              emoji={event.interview.job.emoji}
              caption={JobStatusItems().find((x) => x.id == event.interview!.job.status)?.title}
              behavior={"card"}
            />
          </>
        )}
        <Title header={t("calendar:guests")} size={"xs"} paddingBottom paddingTop />
        <Stack>
          {attendees.map((guest, index) => (
            <ListItem
              key={index}
              preTitle={guest.employee ? renderEmployeeName(guest.employee) : guest.email || ""}
              emphasis={"top"}
              avatarUrl={guest.employee && getEmployeeAvatarUrl(guest.employee, "sm")}
              titleSlot={
                <DotSeparator className={"text-secondary"}>
                  {getDescriptionForAttendee(guest)}
                </DotSeparator>
              }
              captionSlot={attendeeResponseStatus(guest)}
              forceShowAvatar={true}
            />
          ))}
        </Stack>
        <Title header={t("common:settings")} size={"xs"} paddingBottom paddingTop />
        <ListItem
          preTitle={t("calendar:event_visibility")}
          title={
            event.visibility == ApiEventVisibility.private
              ? t("calendar:event_visibility_private")
              : t("calendar:event_visibility_public")
          }
        />
      </Stack>
    </Modal>
  );
}
